import type { FeatureAccess } from './access';

export interface SceneMenuFile {
  id: string;
  originalName: string;
  type: string;
  extension: string;
  status: string;
  panoramaCount?: number;
}

export type SceneChipKey = 'cloud' | 'pano' | 'bim' | 'dwg' | 'splat' | 'ortho';

export interface SceneMenuEntry {
  key: string;
  label: string;
  hint: string;
  query: string;
  dot: string;
  isDefault: boolean;
  drawing: boolean;
}

export interface SceneMenu {
  chips: { key: SceneChipKey; label: string }[];
  entries: SceneMenuEntry[];
  defaultQuery: string;
}

const DOT = { cloud: '#059669', splat: '#4f46e5', ortho: '#0284c7', tour: '#d97706' };
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The Open Scene menu, decided from the project's files and what the user may
// see. A type the user has no access to never appears — not as an entry, not
// as a chip, not as the base a combination opens on.
export const buildSceneMenu = (files: SceneMenuFile[], access: FeatureAccess): SceneMenu => {
  const ready = files.filter((f) => f.status === 'ready');
  const count = (type: string) => ready.filter((f) => f.type === type).length;
  const ext = (f: SceneMenuFile) => (f.extension || '').toLowerCase();
  const panoramas = ready.reduce((n, f) => n + (f.type === 'point_cloud' ? (f.panoramaCount ?? 0) : 0), 0);
  const clouds = count('point_cloud');
  const splats = access.splat ? count('gaussian') : 0;
  const orthos = access.ortho ? count('orthomosaic') : 0;
  const hasCloud = clouds > 0;
  const hasSplat = splats > 0;
  const hasOrtho = orthos > 0;
  const hasBim = ready.some((f) => f.type === 'three_d' && ['rvt', 'ifc'].includes(ext(f)));
  const drawings = ready.filter((f) => f.type === 'two_d' && ['dwg', 'dxf'].includes(ext(f))).slice(0, 3);

  const baseQs = hasCloud ? '' : hasSplat ? 'base=splat' : hasOrtho ? 'base=ortho' : '';
  const q = (qs = '') => [qs, baseQs].filter(Boolean).join('&');

  const chips: SceneMenu['chips'] = [];
  if (hasCloud) chips.push({ key: 'cloud', label: plural(clouds, 'point cloud') });
  if (panoramas > 0) chips.push({ key: 'pano', label: plural(panoramas, 'panorama') });
  if (count('three_d') > 0) chips.push({ key: 'bim', label: `${count('three_d')} BIM` });
  if (count('two_d') > 0) chips.push({ key: 'dwg', label: plural(count('two_d'), 'drawing') });
  if (hasSplat) chips.push({ key: 'splat', label: plural(splats, 'splat') });
  if (hasOrtho) chips.push({ key: 'ortho', label: `${orthos} ortho` });

  const entries: SceneMenuEntry[] = [];
  const add = (e: Omit<SceneMenuEntry, 'isDefault' | 'drawing'> & Partial<Pick<SceneMenuEntry, 'isDefault' | 'drawing'>>) =>
    entries.push({ isDefault: false, drawing: false, ...e });

  if (hasCloud && panoramas > 0) add({ key: 'tour', label: 'Virtual tour', hint: 'start from the nearest panorama', query: q('tour=1'), dot: DOT.tour });
  if (hasCloud) add({ key: 'cloud', label: 'Point cloud', hint: 'all layers, default view', query: q(), dot: DOT.cloud, isDefault: true });
  if (hasCloud && hasBim) add({ key: 'cloud-bim', label: 'Point cloud + BIM', hint: 'models drawn over the point cloud', query: q('bim=all'), dot: DOT.cloud });
  if (hasSplat) add({ key: 'splat', label: 'Photoreal (Gaussian splat)', hint: 'splat instead of the point cloud', query: q('base=splat'), dot: DOT.splat, isDefault: !hasCloud });
  if (hasOrtho) add({ key: 'ortho', label: 'Orthomosaic', hint: 'aerial image as the ground, nothing else on', query: q('base=ortho'), dot: DOT.ortho, isDefault: !hasCloud && !hasSplat });
  if (hasOrtho && hasBim) add({ key: 'ortho-bim', label: 'Orthomosaic + BIM', hint: 'models placed on the aerial image', query: q('base=ortho&bim=all'), dot: DOT.ortho });

  if (hasCloud || hasSplat || hasOrtho) {
    const baseLabel = hasCloud ? 'Point cloud' : hasSplat ? 'Photoreal' : 'Orthomosaic';
    const baseDot = hasCloud ? DOT.cloud : hasSplat ? DOT.splat : DOT.ortho;
    for (const d of drawings) {
      add({ key: `drawing-${d.id}`, label: `${baseLabel} + drawing`, hint: d.originalName, query: q(`right=${d.id}`), dot: baseDot, drawing: true });
    }
  }

  return { chips, entries, defaultQuery: q() };
};
